import { Component, Inject, OnInit } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { AuthQuery } from '@app/auth/auth.query';
import { MitarbeiterService } from '../../../services/mitarbeiter.service';
import { Kontrolltermin } from '../../../services/kontrolltermin.service';
import { ConfirmationDialogComponent } from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { environment } from '../../../../environments/environment';

export interface BulkEditDialogData {
  count: number;
}

export type BulkEditResult =
  | { action: 'update'; changes: Partial<Kontrolltermin> }
  | { action: 'delete' };

// Status changes allowed from the Geplant tab
const ALLOWED_STATUS_IDS = [4, 2];

@Component({
  selector: 'app-bulk-edit-kontrolltermin-dialog',
  templateUrl: './bulk-edit-kontrolltermin-dialog.component.html',
  styleUrls: ['./bulk-edit-kontrolltermin-dialog.component.scss']
})
export class BulkEditKontrollterminDialogComponent implements OnInit {
  isLoading = false;

  technikerOptions: any[] = [];
  statusOptions = (environment.kontrolltermin_status || [])
    .filter(status => ALLOWED_STATUS_IDS.includes(status.id));

  // Empty means "leave unchanged"
  selectedTechnikerId: string | number = '';
  selectedStatusId: string | number = '';
  datumDurchfuerung: string = ''; // ISO format YYYY-MM-DD

  canEditDatum = false;

  constructor(
    private dialogRef: MatDialogRef<BulkEditKontrollterminDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: BulkEditDialogData,
    private dialog: MatDialog,
    private mitarbeiterService: MitarbeiterService,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    private authQuery: AuthQuery
  ) {}

  ngOnInit(): void {
    this.checkPermissions();
    this.loadTechnikers();
  }

  private checkPermissions(): void {
    const role = this.authQuery?.role;
    const roleNum = typeof role === 'string' ? parseInt(role, 10) : role;

    // Prüfdatum: SuperUser, CompAdmin, Büro
    this.canEditDatum = roleNum === 1 || roleNum === 2 || roleNum === 11;
  }

  private loadTechnikers(): void {
    this.isLoading = true;

    this.mitarbeiterService.getTechnicians().subscribe({
      next: (response) => {
        this.technikerOptions = response.data || [];
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error loading techniker:', error);
        this.snackBar.open('Fehler beim Laden der Techniker', 'Schließen', { duration: 3000 });
        this.isLoading = false;
      }
    });
  }

  hasChanges(): boolean {
    return !!this.selectedTechnikerId || !!this.selectedStatusId || !!this.datumDurchfuerung;
  }

  onSave(): void {
    if (!this.hasChanges()) return;

    const changes: Partial<Kontrolltermin> = {};

    if (this.selectedTechnikerId) {
      changes.techniker_id = this.selectedTechnikerId.toString();
    }
    if (this.selectedStatusId) {
      changes.status_id = Number(this.selectedStatusId);
    }
    if (this.datumDurchfuerung && this.canEditDatum) {
      changes.datum_durchfuerung = this.datumDurchfuerung;
    }

    this.dialogRef.close({ action: 'update', changes } as BulkEditResult);
  }

  onDelete(): void {
    const confirmRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: this.translate.instant('APPOINTMENT.BULK_DELETE_TITLE'),
        message: this.translate.instant('APPOINTMENT.BULK_DELETE_MESSAGE', { count: this.data.count }),
        confirmText: this.translate.instant('COMMON.DELETE'),
        cancelText: this.translate.instant('COMMON.CANCEL'),
        confirmColor: 'warn'
      }
    });

    confirmRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.dialogRef.close({ action: 'delete' } as BulkEditResult);
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
